import { formatGel, tetriFromDecimal } from './money';

/**
 * Chart-tooltip money formatter for the Sales widgets.
 *
 * Guards against the tooltip's `value ?? ''` fallback so a missing
 * `entry.value` does not format as `₾ 0.00` — empty / non-numeric inputs return
 * an empty string, letting the tooltip simply omit the value.
 *
 * BOS-162: this used to be its own `Intl.NumberFormat` rounding to **zero**
 * decimals, which rendered `GEL 455` where the same figure read `₾ 455.00`
 * three screens away. Chart series arrive as decimal GEL from the wire, so the
 * value crosses to integer tetri here — by digit position, never `× 100` — and
 * leaves through `formatGel` like every other amount in the app.
 *
 * The `currency` parameter is kept because every caller passes the series'
 * declared currency and dropping it would silently make a future multi-currency
 * chart lie. It is asserted rather than used: the bos-api is single-currency
 * (`Common/Currencies.cs`), and `formatGel` renders lari.
 */
export const formatCurrencyValue = (
    currency: string,
    value: number | string,
): string => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (value === '' || Number.isNaN(numeric)) {
        return '';
    }
    if (currency !== 'GEL') {
        // Nothing in the product produces this today. Rendering a non-GEL
        // series with a ₾ would be worse than rendering it plainly.
        return `${numeric} ${currency}`;
    }
    return formatGel(tetriFromDecimal(numeric));
};
